/**
 * Studio display mode: ONE workout of a program, on a screen the trainees can read.
 *
 * This is not a print preview of the builder. The builder is a trainer's tool — collapsed days,
 * dropdowns, tempo codes — and a trainee standing in front of the rack needs the opposite: the
 * exercises of the workout they are doing right now, numbered, with pictures and the reps, in the
 * studio's own colours. So the board is a separate surface with its own layout rules
 * ({@link ../componenets/new_templates/present/programBoardModel}), and this page is only what
 * wraps it: load, brand, fill the screen, get out of the way.
 *
 * It renders as a fixed overlay ON TOP of the admin shell rather than as a route outside it: the
 * sidebar and header have no business on a wall screen, but the auth guard, the permission guard
 * and the company context absolutely do, and they all live on `/app`.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import {
    Close as CloseIcon,
    Download as DownloadIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon,
    Image as ImageIcon,
} from '@mui/icons-material';

import { getTemplate } from '../services/TemplatesService';
import { enrichTemplateExercises } from '../utils/templateEnrichment';
import type { EnrichedTemplate } from '../utils/templateEnrichment';
import { useCompany } from '../contexts/CompanyContext';
import { buildBoardDays } from '../componenets/new_templates/present/programBoardModel';
import ProgramBoard from '../componenets/new_templates/present/ProgramBoard';
import ProgramBoardStage from '../componenets/new_templates/present/ProgramBoardStage';
import {
    boardFileName,
    exportBoardToPdf,
    exportBoardToPng,
} from '../componenets/new_templates/present/programBoardExport';
import { RTL } from '../cache/CacheRtl';

/** Progress teal — what the board wears when the company never set a brand colour. */
const DEFAULT_BRAND_COLOR = '#01BBA7';

/** How long the controls stay up after the last mouse movement. */
const CONTROLS_IDLE_MS = 3000;

const TemplatePresentPage: React.FC = () => {
    const { templateId, dayIndex } = useParams<{ templateId: string; dayIndex?: string }>();
    const navigate = useNavigate();
    const { company } = useCompany();

    const [template, setTemplate] = useState<EnrichedTemplate | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [controlsVisible, setControlsVisible] = useState(true);

    const overlayRef = useRef<HTMLDivElement>(null);
    const boardRef = useRef<HTMLDivElement>(null);
    const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                setLoading(true);
                const response = await getTemplate(templateId);
                const enriched = await enrichTemplateExercises(response.data);
                if (!cancelled) setTemplate(enriched ?? null);
            } catch (e) {
                console.error('Error loading template for presentation:', e);
                if (!cancelled) setError('שגיאה בטעינת התוכנית');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [templateId]);

    const days = useMemo(() => buildBoardDays(template), [template]);
    // The URL names the workout. An out-of-range index (a link to a day that has since been
    // deleted) falls back to the first workout rather than a blank screen.
    const requestedIndex = Number(dayIndex ?? 0);
    const day = days[Number.isFinite(requestedIndex) ? requestedIndex : 0] ?? days[0];

    const brand = useMemo(
        () => ({
            logoUrl: company?.brandingLogoUrl || null,
            color: company?.brandingColor || DEFAULT_BRAND_COLOR,
            name: company?.brandingAppName || company?.name || null,
        }),
        [company?.brandingLogoUrl, company?.brandingColor, company?.brandingAppName, company?.name]
    );

    const programName = template?.nameHeb || template?.name || null;

    // The board lives in its own tab, so "close" means close the tab. A browser only honours that
    // for a tab a script opened — someone who pasted the URL themselves gets sent to the program
    // instead of being left staring at a button that does nothing.
    const exitPresentation = useCallback(() => {
        if (document.fullscreenElement) document.exitFullscreen?.();
        window.close();
        setTimeout(() => {
            if (!window.closed) navigate(`/app/templates/${templateId}`);
        }, 150);
    }, [navigate, templateId]);

    const toggleFullscreen = useCallback(() => {
        if (document.fullscreenElement) {
            document.exitFullscreen?.();
        } else {
            overlayRef.current?.requestFullscreen?.();
        }
    }, []);

    useEffect(() => {
        const onChange = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);

    // The controls are chrome on a wall display — they fade out and come back on any movement, so
    // a board left up all day is just the workout.
    const wakeControls = useCallback(() => {
        setControlsVisible(true);
        if (idleTimer.current) clearTimeout(idleTimer.current);
        idleTimer.current = setTimeout(() => setControlsVisible(false), CONTROLS_IDLE_MS);
    }, []);

    useEffect(() => {
        wakeControls();
        return () => {
            if (idleTimer.current) clearTimeout(idleTimer.current);
        };
    }, [wakeControls]);

    const handleExport = useCallback(
        async (kind: 'pdf' | 'png') => {
            const node = boardRef.current;
            if (!node || !day) return;
            try {
                setExporting(true);
                const name = boardFileName(programName, day.title);
                await (kind === 'pdf' ? exportBoardToPdf(node, name) : exportBoardToPng(node, name));
            } catch (e) {
                console.error('Error exporting workout board:', e);
                setError('שגיאה בייצוא הקובץ');
            } finally {
                setExporting(false);
            }
        },
        [day, programName]
    );

    return (
        <div
            ref={overlayRef}
            onMouseMove={wakeControls}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 1300,
                background: '#0A0F1C',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* Controls */}
            <div
                style={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    opacity: controlsVisible ? 1 : 0,
                    pointerEvents: controlsVisible ? 'auto' : 'none',
                    transition: 'opacity 240ms ease',
                }}
            >
                <RTL>
                    <Button
                        onClick={() => handleExport('pdf')}
                        disabled={exporting || !day}
                        startIcon={<DownloadIcon />}
                        sx={{
                            color: '#fff',
                            background: 'rgba(255,255,255,0.08)',
                            fontSize: '13px',
                            borderRadius: '10px',
                            textTransform: 'none',
                            px: 2,
                            '&:hover': { background: 'rgba(255,255,255,0.16)' },
                        }}
                    >
                        {exporting ? 'מייצא...' : 'הורדה כ-PDF'}
                    </Button>
                </RTL>
                <Tooltip title="הורדה כתמונה">
                    <span>
                        <IconButton
                            onClick={() => handleExport('png')}
                            disabled={exporting || !day}
                            sx={{ color: '#fff', background: 'rgba(255,255,255,0.08)' }}
                        >
                            <ImageIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={isFullscreen ? 'יציאה ממסך מלא' : 'מסך מלא'}>
                    <IconButton
                        onClick={toggleFullscreen}
                        sx={{ color: '#fff', background: 'rgba(255,255,255,0.08)' }}
                    >
                        {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                    </IconButton>
                </Tooltip>
                <Tooltip title="סגירה">
                    <IconButton
                        onClick={exitPresentation}
                        aria-label="סגירה"
                        sx={{ color: '#fff', background: 'rgba(255,255,255,0.08)' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Tooltip>
            </div>

            {loading && (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress sx={{ color: DEFAULT_BRAND_COLOR }} />
                </div>
            )}

            {!loading && error && (
                <div
                    style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 16,
                    }}
                >
                    <Typography sx={{ color: '#fff', fontSize: '20px' }}>{error}</Typography>
                    <Button onClick={exitPresentation} sx={{ color: DEFAULT_BRAND_COLOR }}>
                        חזרה לתוכנית
                    </Button>
                </div>
            )}

            {!loading && !error && !day && (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ color: '#fff', fontSize: '20px' }}>
                        לא נמצא אימון להצגה
                    </Typography>
                </div>
            )}

            {!loading && !error && day && (
                <ProgramBoardStage>
                    <ProgramBoard
                        innerRef={boardRef}
                        day={day}
                        programName={programName}
                        brand={brand}
                    />
                </ProgramBoardStage>
            )}
        </div>
    );
};

export default TemplatePresentPage;
